/** Layout blocks (owner feedback F21): how content is arranged, revealed and layered. */
import { GAP_LABELS, L, P, SIZE_LABELS, defineBlock } from './custom-blocks';
import { NOTIFY, ex } from './custom-blocks-kit';

const GAPS = ['none', 'xs', 'sm', 'md', 'lg'] as const;
const gap = (def: (typeof GAPS)[number] = 'md') => P.pick('Gap', 'Abstand', GAPS, def, { labels: GAP_LABELS });
const BUTTON_VARIANTS = ['primary', 'secondary', 'ghost'] as const;
const VARIANT_LABELS = { primary: L('Primary', 'Primär'), secondary: L('Secondary', 'Sekundär'), ghost: L('Quiet', 'Dezent') };

const triggerProps = {
  triggerLabel: P.tpl('Button label', 'Beschriftung der Schaltfläche', { def: 'Open' }),
  triggerIcon: P.icon({ en: 'Button icon', de: 'Symbol der Schaltfläche' }),
  triggerVariant: P.pick('Button style', 'Stil der Schaltfläche', BUTTON_VARIANTS, 'secondary', { labels: VARIANT_LABELS }),
  open: P.state('Open while', 'Offen solange', ['boolean'], { opt: true, hint: L('A yes/no state: set it to true from any action to open this from outside.', 'Ein Ja/Nein-Zustand: eine Aktion kann ihn auf wahr setzen, um das von außen zu öffnen.') }),
};

export const layoutBlocks = {
  columns: defineBlock({
    name: L('Columns', 'Spalten'),
    desc: L('Side-by-side columns with a ratio; they stack on narrow screens.', 'Spalten nebeneinander mit festem Verhältnis; auf schmalen Bildschirmen untereinander.'),
    family: 'layout',
    icon: 'columns-2',
    keywords: ['split', 'two column', 'side by side', 'spalten'],
    summary: 'Arranges its children side by side as columns of a ratio (1-2 = one third and two thirds). Every child is one column.',
    children: 'many',
    props: {
      ratio: P.pick('Ratio', 'Verhältnis', ['1-1', '1-2', '2-1', '1-1-1', '1-2-1', '2-1-1', '1-1-1-1', '3-1', '1-3'] as const, '1-1', {
        labels: {
          '1-1': L('Even halves', 'Zwei gleiche'),
          '1-2': L('One third, two thirds', 'Ein Drittel, zwei Drittel'),
          '2-1': L('Two thirds, one third', 'Zwei Drittel, ein Drittel'),
          '1-1-1': L('Three equal', 'Drei gleiche'),
          '1-2-1': L('Narrow, wide, narrow', 'Schmal, breit, schmal'),
          '2-1-1': L('Wide, narrow, narrow', 'Breit, schmal, schmal'),
          '1-1-1-1': L('Four equal', 'Vier gleiche'),
          '3-1': L('Main and side', 'Haupt- und Nebenspalte'),
          '1-3': L('Side and main', 'Neben- und Hauptspalte'),
        },
      }),
      gap: gap('md'),
      align: P.pick('Vertical alignment', 'Senkrechte Ausrichtung', ['start', 'center', 'end', 'stretch'] as const, 'start', {
        labels: { start: L('Top', 'Oben'), center: L('Centre', 'Mitte'), end: L('Bottom', 'Unten'), stretch: L('Stretch', 'Strecken') },
      }),
      stackBelow: P.pick('Stack below', 'Untereinander unter', ['sm', 'md', 'never'] as const, 'md', {
        labels: { sm: L('A small box (520 px)', 'Kleiner Fläche (520 px)'), md: L('A medium box (760 px)', 'Mittlerer Fläche (760 px)'), never: L('Never', 'Nie') },
        hint: L('Components follow the width of the box they are in, not the window.', 'Komponenten folgen der Breite ihres Platzes, nicht des Fensters.'),
      }),
    },
    presets: [
      { id: 'main-side', name: L('Main and side column', 'Haupt- und Nebenspalte'), icon: 'layout-sidebar', props: { ratio: '3-1' }, keywords: ['sidebar'] },
      { id: 'three', name: L('Three columns', 'Drei Spalten'), icon: 'columns-3', props: { ratio: '1-1-1' } },
    ],
    example: {
      props: { ratio: '2-1' },
      children: [
        ex.card('Plan', 'Plan', [ex.text('Everything the team needs for the week, at a glance.', 'Alles, was das Team für die Woche braucht, auf einen Blick.'), ex.progress('Week', 'Woche', 62)]),
        ex.card('Next', 'Als Nächstes', [ex.muted('Review on Thursday', 'Review am Donnerstag')]),
      ],
    },
  }),

  panel: defineBlock({
    name: L('Panel', 'Bereich'),
    desc: L('One section of an accordion, a wizard, a carousel or a split pane.', 'Ein Abschnitt eines Akkordeons, Assistenten, Karussells oder geteilten Bereichs.'),
    family: 'layout',
    icon: 'box',
    summary: 'A titled container that only lives directly inside accordion, wizard, carousel or split. label = its title or step name; valid = a condition that has to hold before a wizard can go on.',
    children: 'many',
    parents: ['accordion', 'wizard', 'carousel', 'split'],
    props: {
      label: P.tpl('Title', 'Titel', { def: 'Section' }),
      description: P.tpl('Description', 'Beschreibung', { opt: true }),
      icon: P.icon(),
      open: P.bool('Open at the start', 'Anfangs geöffnet', false, { when: { prop: '__parent', in: ['accordion'] } }),
      valid: P.expr('Complete when', 'Abgeschlossen wenn', { expect: 'boolean', opt: true, hint: L('A wizard enables "Next" only while this holds.', 'Ein Assistent erlaubt „Weiter“ nur, solange das gilt.'), when: { prop: '__parent', in: ['wizard'] } }),
    },
    example: { props: { label: L('Section', 'Abschnitt') }, children: [ex.text('Content goes here.', 'Hier steht der Inhalt.')] },
  }),

  split: defineBlock({
    name: L('Split pane', 'Geteilter Bereich'),
    desc: L('Two panes with a draggable divider.', 'Zwei Bereiche mit verschiebbarer Trennlinie.'),
    family: 'layout',
    icon: 'split',
    keywords: ['resizable', 'divider', 'master detail'],
    summary: 'Two panels side by side (or stacked) separated by a draggable divider; the first panel gets "initial" percent of the width. Children are exactly two panel nodes.',
    children: { only: ['panel'] },
    props: {
      direction: P.pick('Direction', 'Richtung', ['horizontal', 'vertical'] as const, 'horizontal', { seg: true, labels: { horizontal: L('Side by side', 'Nebeneinander'), vertical: L('Stacked', 'Übereinander') } }),
      initial: P.num('First pane share', 'Anteil des ersten Bereichs', { def: 35, min: 15, max: 85, int: true, unit: L('%', '%') }),
      minSize: P.num('Smallest pane', 'Kleinster Bereich', { def: 20, min: 5, max: 45, int: true, unit: L('%', '%'), advanced: true }),
      height: P.num('Height', 'Höhe', { def: 280, min: 120, max: 900, int: true, unit: L('px', 'px') }),
    },
    example: {
      props: { initial: 38, height: 200 },
      children: [
        ex.panel('List', 'Liste', [ex.heading('Inbox', 'Eingang', 3), ex.muted('12 open', '12 offen')]),
        ex.panel('Details', 'Details', [ex.text('Select something on the left to see it here.', 'Wählen Sie links etwas aus, um es hier zu sehen.')]),
      ],
    },
  }),

  accordion: defineBlock({
    name: L('Accordion', 'Akkordeon'),
    desc: L('Sections that open and close one by one.', 'Abschnitte, die sich einzeln öffnen und schließen.'),
    family: 'layout',
    icon: 'chevron-down',
    keywords: ['collapse', 'faq', 'disclosure', 'expand'],
    summary: 'A stack of collapsible sections. Children are panel nodes (label = the header). multiple lets several stay open; bind keeps the open panel id in a text state.',
    children: { only: ['panel'] },
    props: {
      multiple: P.bool('Several open at once', 'Mehrere gleichzeitig offen', false),
      variant: P.pick('Look', 'Darstellung', ['bordered', 'flush', 'cards'] as const, 'bordered', {
        labels: { bordered: L('Bordered list', 'Umrandete Liste'), flush: L('Lines only', 'Nur Linien'), cards: L('Separate cards', 'Einzelne Karten') },
      }),
      bind: P.state('Open section is kept in', 'Offener Abschnitt wird gespeichert in', ['text'], { opt: true, advanced: true }),
    },
    example: {
      props: { variant: 'bordered' },
      children: [
        ex.panel('How does it work?', 'Wie funktioniert das?', [ex.text('You pick a week, add your hours and send them for approval.', 'Sie wählen eine Woche, tragen Ihre Stunden ein und senden sie zur Freigabe.')], { open: true }),
        ex.panel('Who approves?', 'Wer gibt frei?', [ex.text('Your manager gets a task in the inbox.', 'Ihre Führungskraft bekommt eine Aufgabe im Eingang.')]),
        ex.panel('Can I change it later?', 'Kann ich es später ändern?', [ex.text('Until it is approved, yes.', 'Bis zur Freigabe, ja.')]),
      ],
    },
  }),

  section: defineBlock({
    name: L('Section', 'Abschnitt'),
    desc: L('A titled block of content with an optional action and a collapse toggle.', 'Ein Inhaltsblock mit Titel, optionaler Aktion und Einklappen.'),
    family: 'layout',
    icon: 'section',
    keywords: ['group', 'header', 'titled'],
    summary: 'Groups content under a title (and description). Optional action button on the right (actionLabel + action). collapsible lets people fold it.',
    children: 'many',
    props: {
      title: P.tpl('Title', 'Titel', { opt: true, def: 'Section' }),
      description: P.tpl('Description', 'Beschreibung', { opt: true }),
      icon: P.icon(),
      divider: P.bool('Line under the header', 'Linie unter der Kopfzeile', true),
      collapsible: P.bool('Can be collapsed', 'Einklappbar', false),
      collapsed: P.bool('Collapsed at the start', 'Anfangs eingeklappt', false, { when: { prop: 'collapsible', in: [true] } }),
      actionLabel: P.tpl('Action label', 'Beschriftung der Aktion', { opt: true }),
      action: P.call('Action', 'Aktion', { when: { prop: 'actionLabel', in: ['__set'] } }),
    },
    example: {
      props: { title: L('Team', 'Team'), description: L('Who is on this project', 'Wer an diesem Projekt arbeitet'), actionLabel: L('Add', 'Hinzufügen') },
      children: [ex.row([ex.badge('Mara', 'Mara', 'accent'), ex.badge('Tim', 'Tim'), ex.badge('Lena', 'Lena')])],
    },
  }),

  wizard: defineBlock({
    name: L('Wizard', 'Assistent'),
    desc: L('A guided flow in steps with Back and Next; the last step can run an action.', 'Ein geführter Ablauf in Schritten mit Zurück und Weiter; der letzte Schritt kann eine Aktion ausführen.'),
    family: 'layout',
    icon: 'arrow-right',
    keywords: ['stepper', 'steps', 'onboarding', 'multi step', 'schritte'],
    summary: 'A multi-step flow. Children are panel nodes (label = step name, valid = condition to continue). The final button runs "submit". bind keeps the current step id in a text state.',
    children: { only: ['panel'] },
    props: {
      variant: P.pick('Step indicator', 'Schrittanzeige', ['numbered', 'dots', 'bar'] as const, 'numbered', { labels: { numbered: L('Numbered', 'Nummeriert'), dots: L('Dots', 'Punkte'), bar: L('Progress bar', 'Fortschrittsbalken') } }),
      submitLabel: P.tpl('Final button', 'Letzte Schaltfläche', { def: 'Finish' }),
      submit: P.call('Runs at the end', 'Wird am Ende ausgeführt'),
      bind: P.state('Current step is kept in', 'Aktueller Schritt wird gespeichert in', ['text'], { opt: true, advanced: true }),
    },
    example: {
      props: { submitLabel: L('Finish', 'Fertig') },
      children: [
        ex.panel('Account', 'Konto', [ex.text('Tell us who you are.', 'Sagen Sie uns, wer Sie sind.')]),
        ex.panel('Team', 'Team', [ex.text('Invite the people you work with.', 'Laden Sie Ihre Kolleginnen und Kollegen ein.')]),
        ex.panel('Done', 'Fertig', [ex.text('Everything is ready.', 'Alles ist bereit.')]),
      ],
    },
  }),

  carousel: defineBlock({
    name: L('Carousel', 'Karussell'),
    desc: L('Slides you can page through, with dots and arrows.', 'Folien zum Durchblättern, mit Punkten und Pfeilen.'),
    family: 'layout',
    icon: 'gallery',
    keywords: ['slides', 'slider', 'gallery', 'swipe'],
    summary: 'Shows one panel at a time with arrows and dots. Children are panel nodes. autoplay seconds (0 = off); it stops under reduced motion.',
    children: { only: ['panel'] },
    props: {
      arrows: P.bool('Arrows', 'Pfeile', true),
      dots: P.bool('Dots', 'Punkte', true),
      loop: P.bool('Start over after the last', 'Nach der letzten von vorn', true),
      autoplay: P.num('Advance every', 'Weiter nach', { def: 0, min: 0, max: 60, int: true, unit: L('s (0 = off)', 's (0 = aus)') }),
    },
    example: {
      props: {},
      children: [
        ex.panel('Plan', 'Planen', [ex.heading('Plan the week', 'Die Woche planen', 3), ex.muted('Pick your priorities.', 'Wählen Sie Ihre Prioritäten.')]),
        ex.panel('Do', 'Umsetzen', [ex.heading('Do the work', 'Die Arbeit tun', 3), ex.muted('Log time as you go.', 'Zeit laufend erfassen.')]),
        ex.panel('Review', 'Rückblick', [ex.heading('Look back', 'Zurückblicken', 3), ex.muted('What went well?', 'Was lief gut?')]),
      ],
    },
  }),

  drawer: defineBlock({
    name: L('Drawer', 'Seitenfenster'),
    desc: L('A button that slides a panel in from the side.', 'Eine Schaltfläche, die ein Seitenfenster einblendet.'),
    family: 'layout',
    icon: 'drawer',
    keywords: ['side panel', 'slide over', 'sheet', 'overlay'],
    summary: 'A trigger button that opens a side drawer with its children. Use open (a boolean state) to open it from an action.',
    children: 'many',
    props: {
      title: P.tpl('Title', 'Titel', { def: 'Details' }),
      side: P.pick('Opens from', 'Öffnet von', ['right', 'bottom'] as const, 'right', { seg: true, labels: { right: L('Right', 'Rechts'), bottom: L('Bottom', 'Unten') } }),
      size: P.pick('Width', 'Breite', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
      ...triggerProps,
    },
    example: {
      props: { title: L('Details', 'Details'), triggerLabel: L('Open details', 'Details öffnen') },
      children: [ex.text('Everything about this item.', 'Alles zu diesem Eintrag.'), ex.progress('Progress', 'Fortschritt', 40)],
    },
  }),

  modal: defineBlock({
    name: L('Dialog', 'Dialog'),
    desc: L('A button that opens a dialog window over the page.', 'Eine Schaltfläche, die ein Dialogfenster öffnet.'),
    family: 'layout',
    icon: 'copy',
    keywords: ['modal', 'popup', 'window', 'overlay'],
    summary: 'A trigger button that opens a modal dialog with its children. Use open (a boolean state) to open it from an action.',
    children: 'many',
    props: {
      title: P.tpl('Title', 'Titel', { def: 'Dialog' }),
      description: P.tpl('Description', 'Beschreibung', { opt: true }),
      size: P.pick('Width', 'Breite', ['sm', 'md', 'lg'] as const, 'md', { labels: SIZE_LABELS }),
      ...triggerProps,
    },
    example: {
      props: { title: L('Request time off', 'Urlaub beantragen'), triggerLabel: L('New request', 'Neuer Antrag') },
      children: [ex.text('Choose your dates and send the request.', 'Wählen Sie Ihre Tage und senden Sie den Antrag.')],
    },
  }),

  popover: defineBlock({
    name: L('Popover', 'Popover'),
    desc: L('A small floating panel that opens from a button.', 'Ein kleines schwebendes Feld, das sich an einer Schaltfläche öffnet.'),
    family: 'layout',
    icon: 'info',
    keywords: ['tooltip', 'dropdown', 'floating', 'hint'],
    summary: 'A trigger button that opens a small floating panel anchored to it, containing its children.',
    children: 'many',
    props: {
      width: P.num('Width', 'Breite', { def: 280, min: 160, max: 560, int: true, unit: L('px', 'px') }),
      ...triggerProps,
    },
    example: {
      props: { triggerLabel: L('What is this?', 'Was ist das?'), triggerVariant: 'ghost', triggerIcon: 'info' },
      children: [ex.text('Hours are rounded to quarter hours.', 'Stunden werden auf Viertelstunden gerundet.')],
    },
  }),

  sticky: defineBlock({
    name: L('Sticky bar', 'Haftende Leiste'),
    desc: L('A bar that stays at the top while the rest scrolls.', 'Eine Leiste, die oben bleibt, während der Rest scrollt.'),
    family: 'layout',
    icon: 'pin',
    keywords: ['sticky header', 'pinned', 'toolbar'],
    summary: 'A container that sticks to the top of its scrolling area, on a surface with a bottom line. Put a heading and actions in it.',
    children: 'many',
    props: {
      blur: P.bool('Frosted background', 'Mattierter Hintergrund', true),
      border: P.bool('Line below', 'Linie darunter', true),
      pad: P.pick('Padding', 'Innenabstand', ['sm', 'md'] as const, 'sm', { labels: SIZE_LABELS }),
    },
    example: {
      actions: [NOTIFY],
      props: {},
      children: [ex.row([ex.heading('Week 42', 'Woche 42', 3), ex.button('Today', 'Heute')], { justify: 'between' })],
    },
  }),

  spacer: defineBlock({
    name: L('Spacer', 'Abstandhalter'),
    desc: L('Empty space between blocks; can grow to push things apart.', 'Leerer Raum zwischen Blöcken; kann wachsen und Dinge auseinanderschieben.'),
    family: 'layout',
    icon: 'spacer',
    summary: 'Fixed empty space (size) or, with grow, all the free space of a row (pushes the next block to the end).',
    props: {
      size: P.pick('Size', 'Größe', ['xs', 'sm', 'md', 'lg', 'xl'] as const, 'md', { labels: { ...GAP_LABELS, xl: L('Extra large', 'Sehr groß') } }),
      grow: P.bool('Take all free space', 'Allen freien Platz einnehmen', false),
    },
    example: { props: { size: 'md' } },
  }),

  scroll: defineBlock({
    name: L('Scroll area', 'Scrollbereich'),
    desc: L('A box with a fixed height whose content scrolls.', 'Ein Kasten mit fester Höhe, dessen Inhalt scrollt.'),
    family: 'layout',
    icon: 'scroll',
    keywords: ['overflow', 'scrollable', 'max height'],
    summary: 'Limits its children to maxHeight pixels and lets them scroll, with edge shadows that show there is more.',
    children: 'many',
    props: {
      maxHeight: P.num('Height', 'Höhe', { def: 220, min: 80, max: 900, int: true, unit: L('px', 'px') }),
      direction: P.pick('Scrolls', 'Scrollt', ['vertical', 'horizontal'] as const, 'vertical', { seg: true, labels: { vertical: L('Down', 'Vertikal'), horizontal: L('Sideways', 'Horizontal') } }),
      shadows: P.bool('Edge shadows', 'Schatten an den Rändern', true),
    },
    example: {
      props: { maxHeight: 120 },
      children: Array.from({ length: 6 }, (_, i) => ex.text(`Entry number ${i + 1}`, `Eintrag Nummer ${i + 1}`)),
    },
  }),

  aspect: defineBlock({
    name: L('Fixed ratio', 'Festes Seitenverhältnis'),
    desc: L('A box that keeps its proportions, e.g. 16:9, for media and previews.', 'Ein Kasten mit festen Proportionen, z. B. 16:9, für Medien und Vorschauen.'),
    family: 'layout',
    icon: 'image',
    keywords: ['ratio', 'aspect', '16:9', 'media frame'],
    summary: 'A box that keeps a width:height ratio. Its children are centred inside; put an image or a stat in it.',
    children: 'many',
    props: {
      ratio: P.pick('Ratio', 'Verhältnis', ['1/1', '4/3', '3/2', '16/9', '21/9', '3/4', '9/16'] as const, '16/9', {}),
      tone: P.pick('Background', 'Hintergrund', ['subtle', 'surface', 'accent'] as const, 'subtle', { labels: { subtle: L('Subtle', 'Dezent'), surface: L('Surface', 'Fläche'), accent: L('Accent tint', 'Akzenttönung') } }),
      rounded: P.bool('Rounded corners', 'Runde Ecken', true),
    },
    example: { props: { ratio: '16/9' }, children: [ex.stat('Preview', 'Vorschau', '"16:9"')] },
  }),

  toolbar: defineBlock({
    name: L('Toolbar', 'Werkzeugleiste'),
    desc: L('A header row with a title on the left and actions on the right.', 'Eine Kopfzeile mit Titel links und Aktionen rechts.'),
    family: 'layout',
    icon: 'sliders',
    keywords: ['header', 'actions row', 'bar'],
    summary: 'A row with an optional title and subtitle at the start; its children (buttons, menus, search) sit at the end and wrap on narrow screens.',
    children: 'many',
    props: {
      title: P.tpl('Title', 'Titel', { opt: true }),
      subtitle: P.tpl('Subtitle', 'Untertitel', { opt: true }),
      border: P.bool('Line below', 'Linie darunter', false),
    },
    example: {
      actions: [NOTIFY],
      props: { title: L('Time off', 'Urlaub'), subtitle: L('Requests of your team', 'Anträge Ihres Teams') },
      children: [ex.button('Filter', 'Filtern', 'ghost'), ex.button('New request', 'Neuer Antrag', 'primary')],
    },
  }),
};
